/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { FC, useCallback, useState } from "react";
import { ToolbarTitle } from "../../../../ui/components/Toolbar/ToolbarTitle";
import { FieldNumber } from "../../../../ui/components/Fields/FieldNumber";
import { FieldDropdown, TFieldDropdownOption } from "../../../../ui/components/Fields/FieldDropdown";
import { Panel, PANEL_STYLE } from "../../../../ui/components/Panel";
import { FieldColor, IFieldColorProps } from "../../../../ui/components/Fields/FieldColor";
import { IconList, IIconListProps } from "../../../../ui/components/IconList";
import { IIconListPack } from "../../../../ui/components/IconList/IconListTypes";
import { ICON_SIZE } from "../../../../ui/components/Icon";
import { FieldFontSize, IFieldFontSizeProps } from "../../../../ui/components/Fields/FieldFontSize";
import { FONT_SIZE } from "@adapptio/lib-components";
import { FieldCode } from "../../../../ui/components/Fields/FieldCode";

export interface ITestModuleInspectorProps {}

const options: TFieldDropdownOption[] = [
	{
		label: "Emily",
		value: "Brantleyville"
	},
	{
		label: "Nona",
		value: "Bowie",
		icon: "mdi/cog",
		description: "Some description"
	},
	{
		label: "Leanna",
		value: "Spokane"
	},
	{
		isSeparator: true
	},
	{
		label: "Kate",
		value: "Dante",
		description: "Some description"
	},
	{
		label: "Simpson",
		value: "Rowe"
	},
	{
		label: "Briana",
		value: "Florence"
	},
	{
		label: "Jewel",
		value: "Virgie"
	},
	{
		isSeparator: true
	},
	{
		label: "Sofia",
		value: "Flintville"
	},
	{
		label: "Sheri",
		value: "Crumpler",
		description: "Some description"
	},
	{
		label: "Valeria",
		value: "Kerby",
		description: "Some description"
	},
	{
		label: "Higgins",
		value: "Tioga"
	},
	{
		label: "Elliott",
		value: "Klondike"
	},
	{
		label: "Tia",
		value: "Newkirk"
	},
	{
		isSeparator: true
	},
	{
		label: "Tamra",
		value: "Alderpoint"
	},
	{
		label: "Peterson",
		value: "Malott"
	},
	{
		label: "Linda",
		value: "Jardine"
	},
	{
		label: "Paul",
		value: "Shawmut"
	}
];

const fontSizeContants: IFieldFontSizeProps["constants"] = [
	...Object.keys(FONT_SIZE).map((fontSizeKey) => ({
		label: fontSizeKey,
		value: fontSizeKey
	})),
	{ label: "Custom", value: "2rem" }
];

const wrapperStyle: React.CSSProperties = {
	padding: "0 6px"
};

const colorConstants: IFieldColorProps["constants"] = [
	{ label: "Primary", value: "PRIMARY" }, // -> var(--color-primary); var(--color-${value.toLowerCase()})
	{ label: "Secondary", value: "SECONDARY" },
	{ label: "Warning", value: "#D88629" },
	{ label: "Error", value: "#DD0B1A" },
	{ label: "Box Background", value: "#FFFFFF" }
];

const iconPacks: IIconListPack[] = [
	{
		name: "mdi",
		label: "Material Design",
		shortLabel: "MDI",
		icons: [
			{
				name: "ab-testing",
				title: "ab-testing",
				tags: [ "Developer / Languages" ]
			},
			{
				name: "abacus",
				title: "abacus",
				tags: [ "Math" ]
			},
			{
				name: "abjad-arabic",
				title: "abjad-arabic",
				tags: [ "Alpha / Numeric" ]
			},
			{
				name: "abjad-hebrew",
				title: "abjad-hebrew",
				tags: [ "Alpha / Numeric" ]
			},
			{
				name: "abugida-devanagari",
				title: "abugida-devanagari",
				tags: [ "Alpha / Numeric" ]
			},
			{
				name: "abugida-thai",
				title: "abugida-thai",
				tags: [ "Alpha / Numeric" ]
			},
			{
				name: "access-point",
				title: "access-point",
				tags: []
			},
			{
				name: "access-point-check",
				title: "access-point-check",
				tags: []
			},
			{
				name: "access-point-minus",
				title: "access-point-minus",
				tags: []
			},
			{
				name: "access-point-network",
				title: "access-point-network",
				tags: []
			},
			{
				name: "access-point-network-off",
				title: "access-point-network-off",
				tags: []
			},
			{
				name: "access-point-off",
				title: "access-point-off",
				tags: []
			},
			{
				name: "access-point-plus",
				title: "access-point-plus",
				tags: []
			},
			{
				name: "access-point-remove",
				title: "access-point-remove",
				tags: []
			},
			{
				name: "account",
				title: "account",
				tags: [ "Account / User", "Home Automation" ]
			},
			{
				name: "account-alert",
				title: "account-alert",
				tags: [ "Account / User", "Alert / Error" ]
			}
		]
	}
];

export const TestModuleInspector: FC<ITestModuleInspectorProps> = () => {
	const [ values, setValues ] = useState({
		number: 42,
		select: "",
		combobox: "",
		fontSize: "",
		color: "",
		code: ""
	});
	const [ selectedIcon, setSelectedIcon ] = useState({ packName: "", iconName: "" });
	const [ iconSize, setIconSize ] = useState<ICON_SIZE>();

	const getOnChange = useCallback(
		(item: keyof typeof values) => (value) => {
			setValues((prevState) => ({
				...prevState,
				[item]: value
			}));
		},
		[ setValues ]
	);

	const selectIcon = useCallback<IIconListProps["onSelect"]>((packName, iconName) => {
		setSelectedIcon({ packName, iconName });
	}, []);

	const handleIconSizeChange = useCallback<React.ChangeEventHandler<HTMLInputElement>>((event) => {
		const value = event.currentTarget.value;

		setIconSize(value as ICON_SIZE);
	}, []);

	return (
		<Panel style={PANEL_STYLE.PRIMARY}>
			<div style={wrapperStyle}>
				<br />

				<ToolbarTitle>Inspector</ToolbarTitle>

				<br />

				<div>
					<FieldNumber value={values.number} onChange={getOnChange("number")} />
				</div>

				<br />

				<div>
					<FieldCode value={values.code} onChange={getOnChange("code")} language="javascript" />
				</div>

				<br />

				<div>
					<FieldColor
						value={values.color}
						onChange={getOnChange("color")}
						constants={colorConstants}
					/>
				</div>

				<br />

				<div>
					<FieldDropdown
						value={values.select}
						options={options}
						onChange={getOnChange("select")}
						placeholder="Select something..."
					/>
				</div>

				<br />

				<div>
					<FieldDropdown
						allowCustomInput
						value={values.combobox}
						options={options}
						onChange={getOnChange("combobox")}
					/>
				</div>

				<br />

				<div>
					{Object.keys(ICON_SIZE).map((sizeKey) => (
						<label key={sizeKey}>
							{sizeKey}
							<input
								type="radio"
								name="iconSize"
								value={ICON_SIZE[sizeKey]}
								onChange={handleIconSizeChange}
							/>
						</label>
					))}
				</div>

				<br />

				<div>
					<IconList
						iconPacks={iconPacks}
						selectedIconName={selectedIcon.iconName}
						selectedPackName={selectedIcon.packName}
						onSelect={selectIcon}
						iconPreviewSize={iconSize}
					/>
					<FieldFontSize value="aaa" />
					<FieldFontSize value={values.fontSize} onChange={getOnChange("fontSize")} />
					<FieldFontSize
						value={values.fontSize}
						onChange={getOnChange("fontSize")}
						constants={fontSizeContants}
					/>
				</div>
			</div>
		</Panel>
	);
};
